import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, InputNumber, Space, Spin, Typography, message } from 'antd';
import { ReloadOutlined, ThunderboltOutlined, MailOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const formatCurrency = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const AutofacturaPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [monto, setMonto] = useState(null);

  const fetchPreview = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}?json=1`, {
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'No se pudo cargar el portal');
      }
      setData(json?.data || null);
    } catch (e) {
      setData(null);
      setError(e?.message || 'No se pudo cargar el portal');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPreview();
  }, [token]);

  const alreadyGenerated = Boolean(data?.already_generated);
  const invoiceUuid = data?.invoice?.uuid || '';
  const txMonto = data?.transaction?.monto ?? null;

  const header = useMemo(() => {
    const periodo = data?.periodo || '—';
    return (
      <Space direction="vertical" size={2}>
        <Title level={3} style={{ margin: 0 }}>
          Autoportal de facturación
        </Title>
        <Text type="secondary">Periodo: {periodo}</Text>
      </Space>
    );
  }, [data?.periodo]);

  const handleGenerate = async () => {
    if (!token) return;
    if (!alreadyGenerated) {
      const n = Number(monto || 0);
      if (!(n > 0)) {
        message.error('Ingresa un monto válido mayor a 0');
        return;
      }
    }
    setActionLoading(true);
    try {
      const body = new URLSearchParams();
      if (!alreadyGenerated) {
        body.set('monto', String(Number(monto || 0)));
      }
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: body.toString(),
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'No se pudo generar la factura');
      }
      message.success(json?.message || 'Factura generada');
      await fetchPreview();
    } catch (e) {
      message.error(e?.message || 'No se pudo generar la factura');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      const resp = await fetch(`/api/autofactura/${encodeURIComponent(token)}/send-invoice`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
      });
      const json = await resp.json();
      if (!resp.ok) {
        throw new Error(json?.error || 'No se pudo enviar la factura');
      }
      message.success(json?.message || 'Factura enviada');
    } catch (e) {
      message.error(e?.message || 'No se pudo enviar la factura');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <Card title={header}>
        {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Alert
              type={alreadyGenerated ? 'success' : 'info'}
              showIcon
              message={alreadyGenerated ? 'Factura ya generada para este periodo' : 'Listo para generar'}
              style={{ marginBottom: 16 }}
            />

            <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Concepto">{data?.config?.concepto || '—'}</Descriptions.Item>
              <Descriptions.Item label="Monto">
                {alreadyGenerated ? (
                  formatCurrency(txMonto ?? data?.invoice?.total ?? 0)
                ) : (
                  <InputNumber
                    min={0.01}
                    precision={2}
                    style={{ width: '100%' }}
                    value={monto}
                    onChange={setMonto}
                    placeholder="Ingresa el monto"
                  />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="UUID">{invoiceUuid || '—'}</Descriptions.Item>
            </Descriptions>

            <Space wrap>
              <Button icon={<ReloadOutlined />} onClick={fetchPreview} disabled={loading || actionLoading}>
                Actualizar
              </Button>
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={actionLoading}
                disabled={loading || (!alreadyGenerated && !(Number(monto || 0) > 0))}
              >
                Generar factura
              </Button>
              <Button
                icon={<MailOutlined />}
                onClick={handleSendInvoice}
                loading={actionLoading}
                disabled={!alreadyGenerated || !invoiceUuid || loading}
              >
                Enviar por correo
              </Button>
            </Space>
          </>
        )}
      </Card>
    </div>
  );
};

export default AutofacturaPortal;
